import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import { demandeService, filialeService } from '../services/api';
import ExportExcel from '../components/ExportExcel';
import { LABELS_ROLES } from '../constants/roles';
import MenuProfil from '../components/MenuProfil';
import {
  IconeCloche, IconeDossier, IconeValide, IconeRafraichir, IconeRefuse, IconeArchive,
  IconeListe, IconeNote, IconeAvion, IconeRecu, IconeHorloge, IconeCrayon,
  IconeLoupe, IconeFeu, IconeEclair, IconeReglages, IconeGrille, IconeMenu,
  IconeCible, IconeFermer, IconeGraphique,
} from '../components/IconesSVG';

const STATUTS = {
  brouillon:                  { label: 'Brouillon',           bg: '#F2F3F4', color: '#555' },
  soumis:                     { label: 'Soumis',              bg: '#FEF9E7', color: '#9A7D0A' },
  en_validation_acheteur:     { label: 'Chez Acheteur',       bg: '#FEF9E7', color: '#9A7D0A' },
  en_validation_direction:    { label: 'Chez Direction',      bg: '#FEF9E7', color: '#9A7D0A' },
  en_validation_memo_filiale: { label: 'Prep Memo Filiale',   bg: '#FEF9E7', color: '#9A7D0A' },
  en_validation_dg_filiale:   { label: 'Chez DG Filiale',     bg: '#EAF4FB', color: '#1A5E8A' },
  en_validation_memo_holding: { label: 'Prep Memo Holding',   bg: '#EAF4FB', color: '#1A5E8A' },
  en_attente_pdg:             { label: 'En attente PDG',      bg: '#EAF4FB', color: '#1A5E8A' },
  approuve:                   { label: 'Approuve',            bg: '#EAFAF1', color: '#1E8449' },
  rejete:                     { label: 'Rejete',              bg: '#FDEDEC', color: '#96281B' },
  correction_requise:         { label: 'Correction requise',  bg: '#FDEDEC', color: '#96281B' },
  a_regulariser:              { label: 'A regulariser',       bg: '#FDF2E9', color: '#935116' },
  regularise:                 { label: 'Regularise',          bg: '#EAFAF1', color: '#1E8449' },
  archive:                    { label: 'Archive',             bg: '#F2F3F4', color: '#555' },
};

const TYPES_LABEL = {
  expression_besoin: 'Fiche EB',
  bon_commande:      'Bon de commande',
  memo_filiale:      'Memo Filiale',
  memo_holding:      'Memo Holding',
  frais_mission:     'Frais de mission',
  facture:           'Facture',
  regularisation:    'Regularisation',
};

const STATUTS_PAR_ROLE = {
  responsable_service:      ['soumis'],
  chef_projet:              ['soumis'],
  acheteur:                 ['en_validation_acheteur'],
  direction_achats:         ['en_validation_direction'],
  preparateur_memo_filiale: ['en_validation_memo_filiale'],
  dg_filiale:               ['en_validation_dg_filiale'],
  preparateur_memo_holding: ['en_validation_memo_holding'],
  pdg:                      ['en_attente_pdg'],
};

const NAV_ITEMS = [
  { icon: IconeCloche, label: 'A traiter',        key: 'a_traiter',   badge: true },
  { icon: IconeGrille,  label: 'Tous les docs',    key: 'tous' },
  { icon: IconeDossier,  label: 'Mes documents',   key: 'mes_docs' },
  { icon: IconeValide,  label: 'Approuves',       key: 'approuve' },
  { icon: IconeRafraichir,  label: 'A regulariser',   key: 'a_regulariser' },
  { icon: IconeRefuse,  label: 'Rejetes',         key: 'rejete' },
  { icon: IconeArchive, label: 'Archives',        key: 'archive' },
  { icon: IconeGraphique, label: 'Analyses',       key: 'analyses',    chemin: '/analyses' },
];

const DOSSIERS = [
  { type: 'expression_besoin', label: 'Fiches EB',          icon: IconeListe, color: '#2980B9', bg: '#EAF4FB' },
  { type: 'bon_commande',      label: 'Bons de commande',   icon: IconeNote, color: '#8E44AD', bg: '#F5EEF8' },
  { type: 'frais_mission',     label: 'Frais de mission',   icon: IconeAvion, color: '#E67E22', bg: '#FDF2E9' },
  { type: 'facture',           label: 'Factures',           icon: IconeRecu, color: '#16A085', bg: '#E8F8F5' },
  { type: 'regularisation',    label: 'Regularisations',    icon: IconeRafraichir, color: '#935116', bg: '#FDF2E9' },
  { type: 'tous',              label: 'Tous les documents', icon: IconeDossier, color: '#0D1B2A', bg: '#F5F6FA' },
];

function BarChart({ data, height = 60 }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map(d => d.value), 1);
  const w = 100 / data.length;
  return (
    <svg viewBox={`0 0 100 ${height}`} style={{ width: '100%', height }} preserveAspectRatio="none">
      {data.map((d, i) => {
        const barH = (d.value / max) * (height - 10);
        return (
          <g key={i}>
            <rect x={i * w + w * 0.1} y={height - barH} width={w * 0.8} height={barH} fill={d.color || '#C0392B'} opacity={0.85} rx={2} />
          </g>
        );
      })}
    </svg>
  );
}

function DonutChart({ approuve, enCours, rejete, total }) {
  if (total === 0) return <div style={{ fontSize: 12, color: '#aaa', textAlign: 'center', padding: 20 }}>Aucun document</div>;
  const r = 40, cx = 55, cy = 55, circumference = 2 * Math.PI * r;
  let offset = 0;
  const segments = [
    { p: approuve / total, color: '#27AE60' },
    { p: enCours / total,  color: '#2980B9' },
    { p: rejete / total,   color: '#C0392B' },
  ];
  return (
    <svg viewBox="0 0 110 110" style={{ width: 110, height: 110 }}>
      {segments.map((seg, i) => {
        const dash = seg.p * circumference;
        const gap = circumference - dash;
        const rotate = -90 + offset * 360;
        offset += seg.p;
        return (
          <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={seg.color} strokeWidth={14}
            strokeDasharray={`${dash} ${gap}`} strokeDashoffset={0}
            transform={`rotate(${rotate} ${cx} ${cy})`} />
        );
      })}
      <text x={cx} y={cy - 4} textAnchor="middle" fontSize="16" fontWeight="700" fill="#0D1B2A">{total}</text>
      <text x={cx} y={cy + 12} textAnchor="middle" fontSize="9" fill="#888">docs</text>
    </svg>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  // Redirection auto selon rôle
  useEffect(() => {
    if (!user) return;
    const role = user?.roles?.[0]?.role || user?.role;
    const rolesActeur = ['acheteur','direction_achats','preparateur_memo_filiale','dg_filiale','preparateur_memo_holding','responsable_service'];
    const rolesDemandeur = ['demandeur','chef_projet'];
    if (role === 'pdg') navigate('/parapheur');
    else if (rolesActeur.includes(role) && !user?.isGlobal) navigate('/dashboard-acteur');
    else if (rolesDemandeur.includes(role) && !user?.isGlobal) navigate('/dashboard-demandeur');
  // eslint-disable-next-line react-hooks/exhaustive-deps -- redirection declenchee par le seul changement d'utilisateur
  }, [user]);
  const [demandes, setDemandes] = useState([]);
  const [stats, setStats] = useState({ total: 0, a_traiter: 0, approuve: 0, a_regulariser: 0, rejete: 0, en_cours: 0, montantTotal: 0 });
  const [loading, setLoading] = useState(true);
  const [filtre, setFiltre] = useState('tous');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [vue, setVue] = useState('liste');
  const [vueDossiers, setVueDossiers] = useState(false);
  const [recherche, setRecherche] = useState('');
  const [filtreStatut, setFiltreStatut] = useState('');
  const [filtreType, setFiltreType] = useState('');
  const [filtreFiliale, setFiltreFiliale] = useState('');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [montantMin, setMontantMin] = useState('');
  const [montantMax, setMontantMax] = useState('');
  const [showFiltresAvances, setShowFiltresAvances] = useState(false);
  const [filiales, setFiliales] = useState([]);
  const [recherching, setRecherching] = useState(false);

  const roleUser = user?.roles?.[0]?.role || user?.role;
  const isAdmin = roleUser === 'admin' || user?.isGlobal;

  useEffect(() => {
    chargerDemandes();
    if (isAdmin) filialeService.getAll().then(r => setFiliales(r.data || []));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- rechargement volontairement limite au changement de filtre
  }, [filtre]);

  const chargerDemandes = async () => {
    try {
      setLoading(true);
      let params = {};
      if (filtre === 'a_traiter') {
        const statutsAttendus = STATUTS_PAR_ROLE[roleUser] || [];
        if (statutsAttendus.length > 0) params = { statut: statutsAttendus[0] };
      } else if (filtre === 'mes_docs') {
        params = {};
      } else if (filtre !== 'tous') {
        params = { statut: filtre };
      }
      const res = await demandeService.getAll(params);
      const data = res.data || [];
      let filtered = filtre === 'mes_docs'
        ? data.filter(d => d.demandeur?._id === user?._id || d.demandeur?.id === user?.id)
        : data;
      setDemandes(filtered);
      const enCours = data.filter(d => !['approuve','rejete','archive','brouillon'].includes(d.statut)).length;
      setStats({
        total: res.total || data.length,
        a_traiter: data.filter(d => (STATUTS_PAR_ROLE[roleUser] || []).includes(d.statut)).length,
        approuve: data.filter(d => d.statut === 'approuve').length,
        a_regulariser: data.filter(d => d.statut === 'a_regulariser').length,
        rejete: data.filter(d => d.statut === 'rejete').length,
        en_cours: enCours,
        montantTotal: data.filter(d => d.statut === 'approuve').reduce((s, d) => s + (d.montant || d.montantEstime || 0), 0),
      });
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const lancerRecherche = useCallback(async () => {
    if (!recherche && !filtreStatut && !filtreType && !filtreFiliale && !dateDebut && !dateFin && !montantMin && !montantMax) {
      chargerDemandes(); return;
    }
    setRecherching(true);
    try {
      const params = {};
      if (recherche) params.q = recherche;
      if (filtreStatut) params.statut = filtreStatut;
      if (filtreType) params.type = filtreType;
      if (filtreFiliale) params.filiale = filtreFiliale;
      if (dateDebut) params.dateDebut = dateDebut;
      if (dateFin) params.dateFin = dateFin;
      if (montantMin) params.montantMin = montantMin;
      if (montantMax) params.montantMax = montantMax;
      const res = await demandeService.rechercher(params);
      setDemandes(res.data || []);
    } catch (err) { console.error(err); }
    finally { setRecherching(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- dependances volontairement restreintes : les ajouter relancerait la requete a chaque rendu
  }, [recherche, filtreStatut, filtreType, filtreFiliale, dateDebut, dateFin, montantMin, montantMax]);

  const reinitialiserFiltres = () => {
    setRecherche(''); setFiltreStatut(''); setFiltreType('');
    setFiltreFiliale(''); setDateDebut(''); setDateFin('');
    setMontantMin(''); setMontantMax('');
    setShowFiltresAvances(false);
    chargerDemandes();
  };

  useEffect(() => {
    const timer = setTimeout(() => { if (recherche) lancerRecherche(); }, 400);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- relance volontairement limitee a la saisie (anti-rebond)
  }, [recherche]);

  const formatDate = (date) => date ? new Date(date).toLocaleDateString('fr-FR') : '—';
  const formatMontant = (m) => m ? Number(m).toLocaleString('fr-FR') + ' F' : '—';
  const titreSection = () => {
    const map = { a_traiter: 'A traiter', mes_docs: 'Mes documents', approuve: 'Approuves', a_regulariser: 'A regulariser', rejete: 'Rejetes', archive: 'Archives' };
    return map[filtre] || 'Tous les documents';
  };

  const statutsChartData = [
    { label: 'En cours',  value: stats.en_cours,      color: '#2980B9' },
    { label: 'Approuves', value: stats.approuve,       color: '#27AE60' },
    { label: 'Rejetes',   value: stats.rejete,         color: '#C0392B' },
    { label: 'Regul.',    value: stats.a_regulariser,  color: '#E67E22' },
  ];

  const heure = new Date().getHours();
  const salutation = heure < 12 ? 'Bonjour' : heure < 18 ? 'Bon apres-midi' : 'Bonsoir';

  // ===== VUE DOSSIERS =====
  const renderVueDossiers = () => (
    <div>
      <div style={{ fontSize: 13, color: '#888', marginBottom: 20 }}>
        {salutation} <strong style={{ color: '#0D1B2A' }}>{user?.prenom}</strong> — Selectionnez un dossier
      </div>
      <div className="grille-mobile-2" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 28 }}>
        {DOSSIERS.map(dossier => {
          const docsType = dossier.type === 'tous' ? demandes : demandes.filter(d => d.type === dossier.type);
          const enCours = docsType.filter(d => !['approuve','rejete','archive'].includes(d.statut)).length;
          const approuves = docsType.filter(d => d.statut === 'approuve').length;
          const aCorrection = docsType.filter(d => d.statut === 'correction_requise').length;
          return (
            <div key={dossier.type}
              onClick={() => {
                setVueDossiers(false);
                if (dossier.type !== 'tous') {
                  setFiltreType(dossier.type);
                  lancerRecherche();
                } else {
                  reinitialiserFiltres();
                }
              }}
              style={s.dossierCard}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
                <div style={{ width: 52, height: 52, borderRadius: 14, background: dossier.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26 }}>
                  <dossier.icon taille={26} />
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#0D1B2A' }}>{dossier.label}</div>
                  <div style={{ fontSize: 12, color: '#888', marginTop: 3 }}>
                    {docsType.length} document{docsType.length > 1 ? 's' : ''}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {enCours > 0 && (
                  <span style={{ fontSize: 11, background: '#FEF9E7', color: '#9A7D0A', padding: '3px 10px', borderRadius: 20, fontWeight: 600 }}>
                    <IconeHorloge taille={12} /> {enCours} en cours
                  </span>
                )}
                {approuves > 0 && (
                  <span style={{ fontSize: 11, background: '#EAFAF1', color: '#1E8449', padding: '3px 10px', borderRadius: 20, fontWeight: 600 }}>
                    <IconeValide taille={12} /> {approuves}
                  </span>
                )}
                {aCorrection > 0 && (
                  <span style={{ fontSize: 11, background: '#FDEDEC', color: '#96281B', padding: '3px 10px', borderRadius: 20, fontWeight: 600 }}>
                    <IconeCrayon taille={12} /> {aCorrection} a corriger
                  </span>
                )}
                {docsType.length === 0 && (
                  <span style={{ fontSize: 11, color: '#ccc' }}>Aucun document</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {stats.a_traiter > 0 && (
        <div style={s.alertTraiter} onClick={() => { setVueDossiers(false); setFiltre('a_traiter'); }}>
          <span style={{ display: 'inline-flex' }}><IconeCloche taille={20} /></span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700 }}>Vous avez {stats.a_traiter} document{stats.a_traiter > 1 ? 's' : ''} a traiter</div>
            <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>Cliquez pour voir les documents qui attendent votre action</div>
          </div>
          <span style={{ fontSize: 18 }}>→</span>
        </div>
      )}
      <button onClick={() => navigate('/nouveau-document')} style={s.btnNouvelleGrand}>
        + Nouvelle demande
      </button>
    </div>
  );

  // ===== VUE LISTE =====
  const renderVueListe = () => (
    <>
      {filtre === 'tous' && !recherche && !filtreStatut && (
        <div style={s.welcomeRow}>
          <div style={s.welcomeCard}>
            <div style={s.welcomeText}>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', marginBottom: 4 }}>{salutation}</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#fff', marginBottom: 4 }}>{user?.prenom} {user?.nom}</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>
                {user?.roles?.[0]?.filiale?.nom || 'JETA Groupe'} · {LABELS_ROLES[roleUser] || roleUser || ''}
              </div>
            </div>
            {stats.a_traiter > 0 && (
              <div style={s.welcomeAlert} onClick={() => setFiltre('a_traiter')}>
                <div style={{ fontSize: 22, fontWeight: 700 }}>{stats.a_traiter}</div>
                <div style={{ fontSize: 11 }}>doc{stats.a_traiter > 1 ? 's' : ''} a traiter</div>
                <div style={{ fontSize: 10, opacity: 0.8, marginTop: 2 }}>Voir →</div>
              </div>
            )}
          </div>
          <div style={s.chartCard}>
            <div style={s.chartTitle}>Repartition</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <DonutChart approuve={stats.approuve} enCours={stats.en_cours} rejete={stats.rejete} total={stats.total} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  { label: 'En cours',  value: stats.en_cours, color: '#2980B9' },
                  { label: 'Approuves', value: stats.approuve, color: '#27AE60' },
                  { label: 'Rejetes',   value: stats.rejete,   color: '#C0392B' },
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: item.color, flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: '#555' }}>{item.label}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#0D1B2A', marginLeft: 'auto' }}>{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div style={s.chartCard}>
            <div style={s.chartTitle}>Activite par statut</div>
            <BarChart data={statutsChartData} height={70} />
            <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: 6 }}>
              {statutsChartData.map((d, i) => (
                <div key={i} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: d.color }}>{d.value}</div>
                  <div style={{ fontSize: 9, color: '#aaa' }}>{d.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={s.montantCard}>
            <div style={s.chartTitle}>Montant approuve</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#27AE60', lineHeight: 1.2, marginTop: 8 }}>
              {Number(stats.montantTotal).toLocaleString('fr-FR')}
            </div>
            <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>FCFA total approuve</div>
            <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {[
                { label: 'Total docs',    value: stats.total,         color: '#0D1B2A' },
                { label: 'A regulariser', value: stats.a_regulariser, color: '#E67E22' },
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 11, color: '#888' }}>{item.label}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: item.color }}>{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="grille-mobile-2" style={s.statsRow}>
        {[
          { label: 'Total documents', value: stats.total,         color: '#0D1B2A', icon: IconeDossier, bg: '#F5F6FA', onClick: () => setFiltre('tous') },
          { label: 'A traiter',       value: stats.a_traiter,     color: '#C0392B', icon: IconeCloche, bg: '#FDEDEC', onClick: () => setFiltre('a_traiter') },
          { label: 'Approuves',       value: stats.approuve,      color: '#1E8449', icon: IconeValide, bg: '#EAFAF1', onClick: () => setFiltre('approuve') },
          { label: 'A regulariser',   value: stats.a_regulariser, color: '#935116', icon: IconeRafraichir, bg: '#FDF2E9', onClick: () => setFiltre('a_regulariser') },
        ].map((st, i) => (
          <div key={i} style={{ ...s.statCard, cursor: 'pointer' }} onClick={st.onClick}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={s.statLabel}>{st.label}</div>
                <div style={{ ...s.statValue, color: st.color }}>{st.value}</div>
              </div>
              <div style={{ width: 38, height: 38, borderRadius: 10, background: st.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>
                <st.icon taille={22} />
              </div>
            </div>
            <div style={{ marginTop: 10, height: 3, background: '#F0F0F0', borderRadius: 2 }}>
              <div style={{ height: '100%', background: st.color, borderRadius: 2, width: stats.total > 0 ? Math.min(100, (st.value / stats.total) * 100) + '%' : '0%', opacity: 0.6 }} />
            </div>
          </div>
        ))}
      </div>

      <div style={s.searchBar}>
        <div style={{ position: 'relative', flex: 1 }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#aaa', display: 'flex' }}><IconeLoupe taille={15} /></span>
          <input style={{ ...s.searchInput, paddingLeft: 36 }}
            placeholder="Rechercher par reference, motif, demandeur..."
            value={recherche} onChange={e => setRecherche(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && lancerRecherche()} />
        </div>
        <button onClick={() => setShowFiltresAvances(!showFiltresAvances)} style={s.btnFiltres}>
          <IconeCible taille={13} /> Filtres {showFiltresAvances ? '▲' : '▼'}
        </button>
        <button onClick={lancerRecherche} style={s.btnRecherche} disabled={recherching}>
          {recherching ? '...' : 'Rechercher'}
        </button>
        {(recherche || filtreStatut || filtreType || filtreFiliale || dateDebut || dateFin || montantMin || montantMax) && (
          <button onClick={reinitialiserFiltres} style={s.btnReset}><IconeFermer taille={12} /> Effacer</button>
        )}
      </div>

      {showFiltresAvances && (
        <div style={s.filtresPanel}>
          <div className="grille-mobile-1" style={s.filtresGrid}>
            <div style={s.filtreGroup}>
              <label style={s.filtreLabel}>Statut</label>
              <select style={s.filtreSelect} value={filtreStatut} onChange={e => setFiltreStatut(e.target.value)}>
                <option value="">Tous les statuts</option>
                {Object.entries(STATUTS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div style={s.filtreGroup}>
              <label style={s.filtreLabel}>Type de document</label>
              <select style={s.filtreSelect} value={filtreType} onChange={e => setFiltreType(e.target.value)}>
                <option value="">Tous les types</option>
                {Object.entries(TYPES_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            {isAdmin && (
              <div style={s.filtreGroup}>
                <label style={s.filtreLabel}>Filiale</label>
                <select style={s.filtreSelect} value={filtreFiliale} onChange={e => setFiltreFiliale(e.target.value)}>
                  <option value="">Toutes les filiales</option>
                  {filiales.map(f => <option key={f._id} value={f._id}>{f.code} — {f.nom}</option>)}
                </select>
              </div>
            )}
            <div style={s.filtreGroup}>
              <label style={s.filtreLabel}>Date debut</label>
              <input style={s.filtreSelect} type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)} />
            </div>
            <div style={s.filtreGroup}>
              <label style={s.filtreLabel}>Date fin</label>
              <input style={s.filtreSelect} type="date" value={dateFin} onChange={e => setDateFin(e.target.value)} />
            </div>
            <div style={s.filtreGroup}>
              <label style={s.filtreLabel}>Montant min</label>
              <input style={s.filtreSelect} type="number" placeholder="0" value={montantMin} onChange={e => setMontantMin(e.target.value)} />
            </div>
            <div style={s.filtreGroup}>
              <label style={s.filtreLabel}>Montant max</label>
              <input style={s.filtreSelect} type="number" placeholder="999999999" value={montantMax} onChange={e => setMontantMax(e.target.value)} />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
            <button onClick={lancerRecherche} style={s.btnRecherche}><IconeLoupe taille={13} /> Appliquer</button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ fontSize: 12, color: '#888' }}>
          <strong style={{ color: '#0D1B2A' }}>{demandes.length}</strong> document{demandes.length > 1 ? 's' : ''}
          {(recherche || filtreStatut) && <span style={{ color: '#2980B9', marginLeft: 6 }}>— filtres actifs</span>}
        </div>
      </div>

      {loading || recherching ? (
        <div style={s.empty}>
          <div style={{ marginBottom: 8, color: '#bbb' }}><IconeHorloge taille={28} /></div>
          {recherching ? 'Recherche en cours...' : 'Chargement...'}
        </div>
      ) : demandes.length === 0 ? (
        <div style={s.empty}>
          <div style={{ marginBottom: 14, color: '#ccc' }}><IconeDossier taille={56} /></div>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#0D1B2A' }}>Aucun document trouve</div>
          <div style={{ fontSize: 13, color: '#aaa', marginTop: 6 }}>
            {recherche ? "Essayez avec d'autres mots cles" : 'Creez votre premiere demande'}
          </div>
          {!recherche && (
            <button onClick={() => navigate('/nouveau-document')} style={{ ...s.btnNew, marginTop: 20 }}>
              + Nouvelle demande
            </button>
          )}
        </div>
      ) : vue === 'liste' ? (
        <div style={s.tableWrap}>
          <table className="table-mobile" style={s.table}>
            <thead>
              <tr>
                {['Reference', 'Type', 'Motif', 'Demandeur', 'Filiale', 'Date', 'Montant', 'Statut', ''].map(h => (
                  <th key={h} style={s.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {demandes.map(d => {
                const statut = STATUTS[d.statut] || STATUTS['brouillon'] || STATUTS.brouillon;
                const estMonTour = (STATUTS_PAR_ROLE[roleUser] || []).includes(d.statut);
                return (
                  <tr key={d._id}
                    style={{ ...s.tr, ...(estMonTour ? { background: '#FFFBF0', borderLeft: '3px solid #F0B429' } : { borderLeft: '3px solid transparent' }) }}
                    onClick={() => navigate('/demandes/' + (d._id || d.id))}>
                    <td style={s.td}>
                      <span style={s.ref}>{d.reference || d.ref || '—'}</span>
                      <div style={{ display: 'flex', gap: 3, marginTop: 3, flexWrap: 'wrap' }}>
                        {d.urgence && <span style={s.urgentTag}><IconeFeu taille={10} /> Urgent</span>}
                        {d.isRegularisation && <span style={s.regTag}>Regul.</span>}
                        {estMonTour && <span style={{ ...s.urgentTag, background: '#EAF4FB', color: '#1A5E8A' }}><IconeEclair taille={10} /> Mon tour</span>}
                      </div>
                    </td>
                    <td style={s.td}>
                      <span style={{ fontSize: 11, background: '#F5F6FA', padding: '2px 8px', borderRadius: 10, color: '#555', border: '0.5px solid #E5E7EB' }}>
                        {TYPES_LABEL[d.type] || d.type}
                      </span>
                    </td>
                    <td style={s.td}><div style={s.motifText}>{d.titre || d.motif || '—'}</div></td>
                    <td style={s.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#0D1B2A', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#fff', fontWeight: 600, flexShrink: 0 }}>
                          {d.demandeur?.prenom?.[0]}{d.demandeur?.nom?.[0]}
                        </div>
                        <span style={{ fontSize: 12 }}>{d.demandeur?.prenom} {d.demandeur?.nom}</span>
                      </div>
                    </td>
                    <td style={s.td}>
                      <span style={{ fontSize: 11, fontWeight: 600, background: '#0D1B2A', color: '#fff', padding: '2px 7px', borderRadius: 6 }}>{d.filiale?.code || '—'}</span>
                    </td>
                    <td style={s.td}><div style={{ fontSize: 12, color: '#666' }}>{formatDate(d.createdAt)}</div></td>
                    <td style={s.td}><div style={{ fontSize: 12, fontWeight: 600, color: '#C0392B' }}>{formatMontant(d.montantEstime)}</div></td>
                    <td style={s.td}>
                      <span style={{ ...s.statusPill, background: statut.bg, color: statut.color }}>{statut.label}</span>
                    </td>
                    <td style={s.td} onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button style={s.actionBtn} onClick={() => navigate('/demandes/' + (d._id || d.id))}>Voir</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grille-mobile-2" style={s.grilleWrap}>
          {demandes.map(d => {
            const statut = STATUTS[d.statut] || STATUTS['brouillon'] || STATUTS.brouillon;
            const estMonTour = (STATUTS_PAR_ROLE[roleUser] || []).includes(d.statut);
            return (
              <div key={d._id}
                style={{ ...s.grilleCard, ...(estMonTour ? { borderColor: '#F0B429', borderWidth: 2 } : {}) }}
                onClick={() => navigate('/demandes/' + (d._id || d.id))}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#0D1B2A' }}>{d.reference}</div>
                    <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                      <span style={{ fontSize: 10, fontWeight: 600, background: '#0D1B2A', color: '#fff', padding: '1px 6px', borderRadius: 5 }}>{d.filiale?.code}</span>
                      <span style={{ fontSize: 10, color: '#aaa' }}>{formatDate(d.createdAt)}</span>
                    </div>
                  </div>
                  <span style={{ ...s.statusPill, background: statut.bg, color: statut.color, fontSize: 10 }}>{statut.label}</span>
                </div>
                <div style={{ fontSize: 13, color: '#333', marginBottom: 10, lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                  {d.motif}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '0.5px solid #F0F0F0', paddingTop: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#C0392B' }}>{formatMontant(d.montantEstime)}</div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {d.urgence && <span style={s.urgentTag}><IconeFeu taille={10} /></span>}
                    {estMonTour && <span style={{ ...s.urgentTag, background: '#EAF4FB', color: '#1A5E8A' }}><IconeEclair taille={10} /></span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );

  return (
    <div style={s.app}>
      {/* SIDEBAR */}
      <div style={{ ...s.sidebar, width: sidebarOpen ? 230 : 62 }}>
        <div style={s.sidebarTop}>
          <div style={s.logoRow}>
            <img src="/logo.png" alt="JETA" style={{ height: 28 }} onError={e => { e.target.style.display = 'none'; }} />
            {sidebarOpen && <span style={s.logoText}>JETA GED</span>}
          </div>
          <button onClick={() => setSidebarOpen(!sidebarOpen)} style={s.toggleBtn}>
            {sidebarOpen ? '◀' : '▶'}
          </button>
        </div>

        <nav style={s.nav}>
          {NAV_ITEMS.map(item => (
            <div key={item.key}
              onClick={() => {
                // Certaines entrees mènent a une page dediee plutot qu'a un filtre.
                if (item.chemin) { navigate(item.chemin); return; }
                if (item.key === 'classement') { navigate('/classement'); return; }
                setVueDossiers(false);
                setFiltre(item.key);
                reinitialiserFiltres();
              }}
              style={{ ...s.navItem, ...(filtre === item.key && !vueDossiers ? s.navActive : {}) }}
              title={!sidebarOpen ? item.label : ''}>
              <span style={{ flexShrink: 0, display: 'inline-flex' }}><item.icon taille={17} /></span>
              {sidebarOpen && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: 1 }}>
                  <span style={s.navLabel}>{item.label}</span>
                  {item.badge && stats.a_traiter > 0 && (
                    <span style={s.navBadge}>{stats.a_traiter}</span>
                  )}
                </div>
              )}
            </div>
          ))}
          {sidebarOpen && <div style={s.navSeparator}>Administration</div>}
          {(roleUser === 'admin' || user?.isGlobal) && (
            <div onClick={() => navigate('/admin')} style={s.navItem}>
              <span style={{ display: 'inline-flex' }}><IconeReglages taille={17} /></span>
              {sidebarOpen && <span style={s.navLabel}>Administration</span>}
            </div>
          )}
        </nav>

        <div style={s.sidebarBottom}>
          <ExportExcel role={user?.roles?.[0]?.role || user?.role} />
          <MenuProfil sens="haut" couleurAvatar="#C0392B"
            libelle={sidebarOpen ? (user?.prenom || '') + ' ' + (user?.nom || '') : undefined} />
        </div>
      </div>

      {/* MAIN */}
      <div style={s.main}>
        {/* TOPBAR */}
        <div data-entete style={s.topbar}>
          <div>
            <div style={s.pageTitle}>{vueDossiers ? 'Mes dossiers' : titreSection()}</div>
            <div style={s.pageSub}>
              {user?.roles?.[0]?.filiale?.nom || 'JETA Groupe'} · {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {/* Toggle Liste / Dossiers */}
            <div style={{ display: 'flex', background: '#F5F6FA', borderRadius: 8, padding: 3, border: '0.5px solid #e5e7eb' }}>
              <button onClick={() => setVueDossiers(false)}
                style={{ padding: '6px 14px', borderRadius: 6, border: 'none', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', background: !vueDossiers ? '#fff' : 'transparent', color: !vueDossiers ? '#0D1B2A' : '#888', boxShadow: !vueDossiers ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}>
                <IconeMenu taille={13} /> Liste
              </button>
              <button onClick={() => setVueDossiers(true)}
                style={{ padding: '6px 14px', borderRadius: 6, border: 'none', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', background: vueDossiers ? '#fff' : 'transparent', color: vueDossiers ? '#0D1B2A' : '#888', boxShadow: vueDossiers ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}>
                <IconeDossier taille={13} /> Dossiers
              </button>
            </div>
            {!vueDossiers && (
              <button onClick={() => setVue(vue === 'liste' ? 'grille' : 'liste')} style={s.btnVue}>
                {vue === 'liste' ? <IconeGrille taille={14} /> : <IconeMenu taille={14} />}
              </button>
            )}
            <button onClick={() => navigate('/nouveau-document')} style={s.btnNew}>
              + Nouvelle demande
            </button>
          </div>
        </div>

        <div style={s.body}>
          {vueDossiers ? renderVueDossiers() : renderVueListe()}
        </div>
      </div>
    </div>
  );
}

const s = {
  app: { display: 'flex', minHeight: '100vh', fontFamily: 'Inter, system-ui, sans-serif', background: '#F5F6FA' },
  sidebar: { background: '#0D1B2A', display: 'flex', flexDirection: 'column', transition: '0.2s', flexShrink: 0 },
  sidebarTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 12px', borderBottom: '1px solid rgba(255,255,255,0.08)' },
  logoRow: { display: 'flex', alignItems: 'center', gap: 8 },
  logoText: { color: '#fff', fontWeight: 600, fontSize: 15 },
  toggleBtn: { background: 'transparent', border: 'none', color: '#85B7EB', cursor: 'pointer', fontSize: 12, padding: 4 },
  nav: { flex: 1, padding: '10px 0', overflowY: 'auto' },
  navItem: { display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', cursor: 'pointer', color: '#8899AA', borderRadius: 6, margin: '1px 8px', transition: '0.15s' },
  navActive: { background: '#C0392B', color: '#fff' },
  navLabel: { fontSize: 13 },
  navBadge: { background: '#C0392B', color: '#fff', fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 10, minWidth: 18, textAlign: 'center' },
  navSeparator: { padding: '10px 14px 4px', fontSize: 9, color: '#4A5A6A', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, marginTop: 6 },
  sidebarBottom: { padding: 10, borderTop: '1px solid rgba(255,255,255,0.08)' },
  userCard: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 },
  avatar: { width: 32, height: 32, borderRadius: '50%', background: '#C0392B', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, color: '#fff', flexShrink: 0 },
  userName: { fontSize: 12, fontWeight: 600, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  userRole: { fontSize: 10, color: '#8899AA', textTransform: 'capitalize' },
  logoutBtn: { width: '100%', padding: 8, borderRadius: 6, border: 'none', background: 'rgba(255,255,255,0.06)', color: '#8899AA', cursor: 'pointer', fontSize: 12, textAlign: 'left', fontFamily: 'inherit' },
  main: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  topbar: { background: '#fff', borderBottom: '0.5px solid #e5e7eb', padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  pageTitle: { fontSize: 18, fontWeight: 600, color: '#0D1B2A', letterSpacing: '-0.3px' },
  pageSub: { fontSize: 12, color: '#888', marginTop: 2 },
  btnNew: { padding: '10px 18px', borderRadius: 8, border: 'none', background: '#C0392B', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  btnVue: { padding: '10px 12px', borderRadius: 8, border: '0.5px solid #ddd', background: '#fff', fontSize: 16, cursor: 'pointer' },
  body: { padding: 24, flex: 1, overflowY: 'auto' },
  welcomeRow: { display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr 1fr', gap: 12, marginBottom: 20 },
  welcomeCard: { background: 'linear-gradient(135deg, #0D1B2A 0%, #1A3A5C 100%)', borderRadius: 12, padding: '18px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  welcomeText: {},
  welcomeAlert: { background: 'rgba(192,57,43,0.9)', borderRadius: 10, padding: '12px 16px', textAlign: 'center', color: '#fff', cursor: 'pointer', minWidth: 80 },
  chartCard: { background: '#fff', borderRadius: 12, border: '0.5px solid #e5e7eb', padding: '14px 16px' },
  montantCard: { background: '#fff', borderRadius: 12, border: '0.5px solid #e5e7eb', padding: '14px 16px' },
  chartTitle: { fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 },
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 },
  statCard: { background: '#fff', borderRadius: 10, padding: '14px 16px', border: '0.5px solid #e5e7eb' },
  statLabel: { fontSize: 11, color: '#888', marginBottom: 4, fontWeight: 500 },
  statValue: { fontSize: 28, fontWeight: 700, letterSpacing: '-0.5px' },
  searchBar: { display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center' },
  searchInput: { width: '100%', padding: '9px 12px', borderRadius: 8, border: '0.5px solid #ddd', fontSize: 13, background: '#fff', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' },
  btnFiltres: { padding: '9px 14px', borderRadius: 8, border: '0.5px solid #ddd', background: '#fff', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' },
  btnRecherche: { padding: '9px 16px', borderRadius: 8, border: 'none', background: '#2980B9', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' },
  btnReset: { padding: '9px 12px', borderRadius: 8, border: '0.5px solid #ddd', background: 'transparent', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', color: '#888', whiteSpace: 'nowrap' },
  filtresPanel: { background: '#fff', border: '0.5px solid #e5e7eb', borderRadius: 10, padding: 16, marginBottom: 14 },
  filtresGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 },
  filtreGroup: { display: 'flex', flexDirection: 'column', gap: 4 },
  filtreLabel: { fontSize: 11, fontWeight: 600, color: '#666' },
  filtreSelect: { padding: '7px 10px', borderRadius: 8, border: '0.5px solid #ddd', fontSize: 12, background: '#F9FAFB', fontFamily: 'inherit', outline: 'none' },
  tableWrap: { background: '#fff', borderRadius: 10, border: '0.5px solid #e5e7eb', overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { padding: '10px 14px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: '#888', background: '#F9FAFB', borderBottom: '0.5px solid #e5e7eb', textTransform: 'uppercase', letterSpacing: '0.04em' },
  tr: { borderBottom: '0.5px solid #f0f0f0', cursor: 'pointer' },
  td: { padding: '12px 14px', color: '#0D1B2A', verticalAlign: 'middle' },
  ref: { fontWeight: 600, fontSize: 13, display: 'block' },
  urgentTag: { fontSize: 10, background: '#FDEDEC', color: '#96281B', padding: '2px 6px', borderRadius: 20, display: 'inline-block' },
  regTag: { fontSize: 10, background: '#FDF2E9', color: '#935116', padding: '2px 6px', borderRadius: 20, display: 'inline-block' },
  motifText: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180, fontSize: 13 },
  statusPill: { padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap' },
  actionBtn: { background: 'transparent', border: '0.5px solid #ddd', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' },
  grilleWrap: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 },
  grilleCard: { background: '#fff', borderRadius: 10, border: '0.5px solid #e5e7eb', padding: 16, cursor: 'pointer' },
  empty: { textAlign: 'center', padding: 80, color: '#888' },
  // VUE DOSSIERS
  dossierCard: { background: '#fff', borderRadius: 14, border: '0.5px solid #e5e7eb', padding: 20, cursor: 'pointer', transition: '0.15s' },
  alertTraiter: { display: 'flex', alignItems: 'center', gap: 14, background: 'linear-gradient(135deg, #C0392B, #96281B)', borderRadius: 12, padding: '16px 20px', color: '#fff', cursor: 'pointer', marginBottom: 16 },
  btnNouvelleGrand: { width: '100%', padding: 16, borderRadius: 12, border: 'none', background: 'linear-gradient(135deg, #C0392B, #96281B)', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 15px rgba(192,57,43,0.3)' },
};
